const FALLBACK_SITE = "https://warmhandoffhq.com";
const DEFAULT_IMG = "/assets/who-logo.png";

// Resolve the current origin at runtime so SEO tags reflect the actual host
// (preview, production, or future custom domain). Falls back to the canonical
// production URL during SSR/build where `window` isn't defined.
function getSite() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return FALLBACK_SITE;
}

/**
 * Per-page SEO component. Uses React 19's native head tag hoisting.
 * Renders <title>, <meta>, and Organization JSON-LD when passed structured=true.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMG,
  keywords = "warm handoff, veteran referrals, veteran transition, veteran service coordination, community referral tracking, care coordination for veterans, warm handoff platform, public-sector referral software, nonprofit referral tracking, human services coordination platform",
  structured = false,
}) {
  const SITE = getSite();
  const fullUrl = `${SITE}${path}`;
  const fullImg = image.startsWith("http") ? image : `${SITE}${image}`;
  const ogTitle = title;
  const ogDesc = description;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="WHO — Warm Hand Off" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImg} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDesc} />
      <meta name="twitter:image" content={fullImg} />

      {structured && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "WHO — Warm Hand Off",
              alternateName: "Warm Hand Off",
              description:
                "Warm-handoff infrastructure for veteran and community transition support.",
              url: SITE,
              logo: `${SITE}/assets/who-logo.png`,
              slogan: "If not you, then WHO.",
              foundingLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Pittsburgh",
                  addressRegion: "PA",
                  addressCountry: "US",
                },
              },
              founder: {
                "@type": "Person",
                name: "Marlon L. Ferguson",
                jobTitle: "Founder & Chief Executive Officer",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  email: "marlon@warmhandoffhq.com",
                  contactType: "customer support",
                  areaServed: "US",
                },
              ],
            }),
          }}
        />
      )}
    </>
  );
}
